import { Box, Theme, Typography } from '@mui/material'
import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { SettingsListItem } from '@/shared/ui/SettingsListItem'
import { useTranslation } from 'react-i18next'
import {
    Help,
    PersonIcon,
    Profile as ProfileIcon,
    TAndC,
    UserIcon,
} from '@/shared/ui/Icons'
import { LogoutButton } from '@/shared/ui/LogoutButton'
import { ProfileScreenModal } from '@/widgets/ProfileScreen/UI/ProfileScreenModal'
import { useAppStore, useVisibilityStore } from '@/shared/store'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorFallback } from '@/shared/ui'
import { defaultProfile, profilePagePath } from '@/shared/constants'
import { Profile } from '@/shared/store/useAppStore'
import { isStreamIQ, isToya } from '@/shared/lib'

export const SettingsPopper = ({ onClose }: { onClose: () => void }) => {
    const ref = useRef<HTMLDivElement | null>(null)
    const { t: translate } = useTranslation()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [profile, setProfile] = useState<Profile | null>(null)
    const { actualProfile, profiles, faq, tAndC, userEmail } = useAppStore()
    const { setSettingsContentVisible } = useVisibilityStore()

    const settingsData: {
        icon: ReactNode
        name: string
        link: string
    }[] = [
        ...(isToya()
            ? [
                  {
                      icon: <ProfileIcon />,
                      name: translate('myProfile'),
                      link: profilePagePath,
                  },
              ]
            : []),
        {
            icon: <TAndC />,
            name: translate('T&C'),
            link: tAndC,
        },
        {
            icon: <Help />,
            name: 'FAQ',
            link: faq,
        },
    ]

    const handleSettingClick = () => {
        onClose()
        setSettingsContentVisible()
    }

    const renderSettings = () =>
        settingsData.map((item) => (
            <SettingsListItem
                key={item.name}
                icon={item.icon}
                name={item.name}
                onClose={handleSettingClick}
                link={item.link}
            />
        ))

    const userNameStyle = {
        display: 'block',
        typography: 'text2',
        lineHeight: '17px',
        height: '17px',
        color: 'custom.greyFontColor',
        paddingLeft: '12px',
    }

    const profileStyle = {
        display: 'flex',
        alignItems: 'center',
        padding: '0 4px 16px',
        borderBottom: (theme: Theme) =>
            `1px solid ${theme.palette.custom.neutralDark}`,
        cursor: 'pointer',
    }

    const showModal = (value: boolean) => {
        setIsModalOpen(value)
    }

    const closeAll = () => {
        setIsModalOpen(false)
        onClose()
    }

    useEffect(() => {
        if (isToya()) {
            const updatedProfile = profiles.find(
                (profile) => profile.id === actualProfile?.id
            )
            setProfile(updatedProfile || defaultProfile)
        }
    }, [profiles.length, profile?.id, actualProfile?.id, userEmail])

    useEffect(() => {
        const id = setTimeout(() => {
            if (isStreamIQ() && !profiles.length) {
                showModal(true)
            }
        }, 300)

        if (isStreamIQ() && profiles.length) {
            const updatedProfile = profiles.find(
                (profile) => profile.id === actualProfile?.id
            )
            updatedProfile && setProfile(updatedProfile)
        }

        return () => clearTimeout(id)
    }, [profiles.length])

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Box ref={ref}>
                <Box sx={profileStyle} onClick={() => showModal(true)}>
                    <Box display="flex" alignItems="center">
                        {profile?.isKid ? <UserIcon /> : <PersonIcon />}
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" sx={userNameStyle}>
                            {profile?.profile_name}
                        </Typography>
                        {userEmail && (
                            <Typography sx={userNameStyle}>
                                {userEmail}
                            </Typography>
                        )}
                    </Box>
                </Box>
                {renderSettings()}
                <LogoutButton onClose={onClose} />
                <ProfileScreenModal
                    shouldShowModal={isModalOpen}
                    setShouldShowModal={closeAll}
                />
            </Box>
        </ErrorBoundary>
    )
}
